<template>
    <div class="video-admin">
        <file-progress></file-progress>
        <div class="col-xs-12" v-if="video">
            <div class="panel col-xs-12 col-md-8 video-player-wrapper">
                <breadcrumbs :breadcrumbs="[
                    {title: 'Dashboard', link: '/'},
                    {title: path, link:  $route.fullPath},
                    {title: video.course.name, link:'/course-admin/' + courseId + '/' + instanceId + '?path_name=' + path + '&term_name=' + term},
                    {title: video.title, link: $route.fullPath},
                    ]">
                </breadcrumbs>
                <br>
                <div class="clear-me">You are editing:</div>
                <div class="sku">
                    {{video.sku}}
                </div>
                <div v-if="!unsyncedSKUs.includes(video.sku) && videoTerm && new Date(videoTerm.endDate) > new Date()">
                    <div v-if="syncedLessons.length">Which will also edit the same lesson in:</div>
                    <ul class="synced-lessons">
                        <li v-for="lesson in syncedLessons">
                            <router-link :to="{ name: 'videoChapters', 
                                    params: { courseId: lesson.course_instance.course, instanceId: lesson.course_instance.id, videoId: lesson.id },
                                    query: {path: getPathForCourse(lesson.course_instance.associated.id), term: lesson.course_instance.associated_term.name, week: lesson.week}
                            }" target="_blank">
                                {{lesson.course_instance.associated_term.name}}, {{getPathForCourse(lesson.course_instance.associated.id)}}, {{lesson.course_instance.associated.name}}, Week {{lesson.week}}
                            </router-link>
                        </li>
                    </ul>
                </div>
                <div class="student-view"><button class="btn btn-success" @click.prevent="openStudentView()">Student View</button></div>
                <div class="panel-heading">
                    <h2>{{video.title}} &mdash; Week {{video.week}}</h2>
                </div>
                <div class="panel-body">
                    <poly-video ref="videoPlayer" :key="selectedChapter.id" :sources="[ videoPlayerUrl ]" :vjsOptions="videoPlayerVjsOptions" :persistPlaybackSpeed="true" :persistPlaybackQuality="true" @play="getComments()" @ready="getAnnotations($event), getMarkers($event)" @timeupdate="onPlayerTimeupdate($event)" v-if="selectedChapter">
                    </poly-video>
                    <img v-else src="./../assets/img/video-admin-placeholder.png" alt="Video Player" class="video-player-placeholder">
                    <div class="tabs clearfix">
                        <ul>
                            <li v-bind:class="{'active': showTab==='description'}" @click="selectedTab = 'description'">
                                Description
                                <span class="required" v-if="video.lesson_files.length === 0 && requires('files')">!</span>
                            </li>
                            <li v-bind:class="{'active': showTab==='exercise'}" @click="selectedTab = 'exercise'">
                                Exercise
                                <span class="required" v-if="!video.exercise && requires('exercise')">!</span>
                            </li>
                            <li v-bind:class="{'active': showTab==='assignment'}" @click="selectedTab = 'assignment'">
                                Assignment
                                <span class="required" v-if="!video.assignment && requires('assignment')">!</span>
                            </li>
                            <li v-bind:class="{'active': showTab==='comments'}" @click="selectedTab = 'comments'">
                                Comments
                                <span class="badge" v-if="comments">{{comments.length}}</span>
                            </li>
                            <li v-bind:class="{'active': showTab==='resources'}" @click="selectedTab = 'resources'">
                                Resources
                                <span class="required" v-if="video.resources.length === 0 && requires('resources')">!</span>
                            </li>
                            <li v-bind:class="{'active': showTab==='transcript'}" @click="selectedTab = 'transcript'">Live Transcript</li>
                        </ul>
                    </div>
                </div>
                <div class="panel" v-show="selectedTab==='description'">
                    <div class="panel-heading">
                        <h2>Lesson Description &#38; Files</h2>
                    </div>
                    <div class="panel-body">
                        <h4>Lesson Description:</h4>
                        <p>The Lesson Description is the full outline for this course's weekly lesson. It should outline everything and be the expanded version of the weekly summary. Things to include would be what is covered this week and expected, for this course.</p>
                        <vue-html5-editor @change="updateVideoDescription" :content="videoDescription"></vue-html5-editor>
                        <button @click.prevent="saveDescription()" class="btn btn-primary">Save Lesson Description</button>
                        <div v-if="requires('files')">
                            <h4>Lesson Files:</h4>
                            <p>Lesson Files are files for the lesson that wouldn't be 'course resources' or respective 'exercise files' - usually just the course outline as PDF, or anything else referenced from the videos directly, scene files, source materials etc.</p>
                            <table v-if="video && video.lesson_files" class="assignment-files table table-striped ">
                                <tbody>
                                    <tr v-for="(file, index) in video.lesson_files" class="list-group-item">
                                        <td><a :href="file.url" _target="blank">{{file.url | filenameFromUrl}}</a></td>
                                        <td><button @click.prevent="deleteLessonFile(file.id, index)" class="btn btn-xs btn-danger">Delete</button></td>
                                    </tr>
                                </tbody>
                            </table>
                            <dropzone id="lessonFiles" ref="lessonFiles" url="#" v-bind:use-custom-dropzone-options="true" v-bind:thumbnail-height="0" v-bind:dropzone-options="dropzoneOptions">
                                <input type="hidden" name="token" value="xxx">
                            </dropzone>
                            <button @click.prevent="addLessonFiles()" class="btn btn-primary">Upload Lesson Files</button>
                        </div>
                        <div class="text-right" v-if="video.lesson_files.length < 1">
                            <div class="checkbox-wrapper" @click.prevent="toggleRequirement('files')">
                                There are no lesson files this week
                                <i class="fa fa-square-o" v-if="requires('files')"></i>
                                <i class="fa fa-check-square-o" v-if="!requires('files')"></i>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="panel" v-if="selectedTab==='assignment'">
                    <div class="panel-heading assignments">
                        <h2><img src="../assets/img/assignment-icon.png">Assignments</h2>
                    </div>
                    <div class="panel-body">
                        <div v-if="video.assignment">
                            <form>
                                <div class="form-group">
                                    <b>Available: </b>
                                    <input class="form-control" type="date" v-model="video.assignment.availableDate">
                                </div>
                                <div class="form-group">
                                    <b>Due: </b>
                                    <input class="form-control" type="date" v-model="video.assignment.dueDate">
                                </div>
                                <div class="form-group">
                                    <h5>Final Project</h5>
                                    <label>Yes <input type="checkbox" v-model="video.assignment.isFinalProject"></label>
                                </div>
                                <div class="form-group">
                                    <h5>Name</h5>
                                    <input class="form-control" type="text" v-model="video.assignment.name">
                                </div>
                                <div class="form-group">
                                    <h5>Difficulty Level</h5>
                                    <select v-model="video.assignment.difficulty" class="form-control">
                                        <option v-for="i in (1, 5)" :value="i">{{i}}</option>
                                    </select>
                                </div>
                                <div class="form-group">
                                    <h5>Points</h5>
                                    <input class="form-control" type="number" v-model="video.assignment.points">
                                </div>
                                <div class="form-group">
                                    <h5>Description</h5>
                                    <vue-html5-editor @change="changeAssignmentDescription" :content="video.assignment.description" :height="200"></vue-html5-editor>
                                </div>
                                <div class="form-group">
                                    <h5>Assignment Image</h5>
                                    <img :src="video.assignment.image">
                                    <input class="form-control" type="file" @change="changeAssignmentImage($event)">
                                </div>
                                <div class="form-group">
                                </div>
                                <h5>Files</h5>
                                <p><b>If you watermark a file larger than 100MB in size, please inform development/IT afterwards so they can upload a pre-processed copy to the server</b></p>
                                <h6 v-if="video.assignment.watermarkingInProgress">Watermarking in progress.</h6>
                                <table class="assignment-files table table-striped ">
                                    <tbody>
                                        <tr v-for="(file, index) in video.assignment.files" class="list-group-item">
                                            <td><a :href="file" _target="blank">{{file | filenameFromUrl}}</a></td>
                                            <td v-if="!video.assignment.watermarkingInProgress"><button @click.prevent="deleteFile('assignment', index)" class="btn btn-xs btn-danger">Delete</button></td>
                                            <td>
                                                <button v-if="!video.assignment.watermarkingInProgress" class="btn btn-xs btn-success" @click.prevent="protectFile('assignment', video.assignment, file)">
                                                    Protect</button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <div class="form-group">
                                    <input class="form-control" type="file" accept="application/zip" @change="uploadAssignmentFiles($event)">
                                </div>
                                <h5>Watermarked Files</h5>
                                <table class="assignment-files table table-striped ">
                                    <tbody>
                                        <tr v-for="(file, index) in video.assignment.watermarkedFiles" class="list-group-item">
                                            <td>
                                                <a @click.prevent="downloadFile(file, 'assignment', video.assignment.id)" href="#">{{file | filenameFromUrl}}</a>
                                                <span v-if="isDirector">(<a @click.prevent="downloadFile(file, 'assignment', video.assignment.id, true)" href="#">source</a>)</span>
                                            </td>
                                            <td><button @click.prevent="deleteWatermarkedFile(file, 'assignment', video.assignment.id)" class="btn btn-xs btn-danger">Delete</button></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </form>
                            <div class="buttons">
                                <div class="col-sm-6">
                                    <button @click.prevent="updateAssignment()" class="btn btn-success">Update</button>
                                </div>
                                <div class="col-sm-6 text-right">
                                    <button class="btn btn-danger" @click.prevent="deleteAssignment(video.assignment.id)">Delete</button>
                                </div>
                            </div>
                        </div>
                        <div v-else>
                            <form v-if="requires('assignment')" @submit.prevent="addAssignment()">
                                <h4 class="create-assignment-title">Create a New Assignment</h4>
                                <p>You can create new assignments below:</p>
                                <div class="form-group">
                                    <i class="clippie clippie-float-right fa fa-info-circle" v-tooltip="'Enter Assignment Name on white line'"></i>
                                    <input type="text" class="form-control" v-model="assignment.name" placeholder="Name" required>
                                </div>
                                <div class="form-group">
                                    <i class="clippie clippie-float-right fa fa-info-circle" v-tooltip="'Add the assignment description here, you can use bullets and bolds to outline requirements etc'"></i>
                                    <vue-html5-editor @change="updateAssignmentDescription" :content="assignment.description" :height="200"></vue-html5-editor>
                                </div>
                                <div class="form-group">
                                    <i class="clippie clippie-float-right fa fa-info-circle" v-tooltip="'Points are a Populi thing, they are all relative so 3 assignments set to 5, 5, 7 will weigh the last one more for marks'"></i>
                                    <input type="number" class="form-control" v-model="assignment.points" placeholder="Points" required>
                                </div>
                                <div class="form-group">
                                    <i class="clippie clippie-float-right fa fa-info-circle" v-tooltip="'Difficult level is for us to graph a week work load. This should be in your Realtimeboard'"></i>
                                    <label>Difficulty Level</label>
                                    <select v-model="assignment.difficulty" class="form-control">
                                        <option v-for="i in (1, 5)" :value="i">{{i}}</option>
                                    </select>
                                </div>
                                <div class="form-group">
                                    <h5>Final Project</h5>
                                    <label>Yes <input type="checkbox" v-model="assignment.isFinalProject"></label>
                                </div>
                                <div class="form-group"><br />
                                    <i class="clippie clippie-float-right fa fa-info-circle" v-tooltip="'If it is a longer assignment you can set how many weeks before it is due, default is one week'"></i>
                                    <label for="multiweek"><input type="checkbox" id="multiweek" @change="isMultiWeek=!isMultiWeek">Multi-week Assignment</label>
                                </div>
                                <div class="form-group" v-if="isMultiWeek">
                                    <label>Duration in Weeks</label>
                                    <select class="form-control" v-model="assignment.duration">
                                        <option v-for="i in (1, 14)">{{i}}</option>
                                    </select>
                                </div>
                                <i class="clippie clippie-float-right fa fa-info-circle" v-tooltip="'Drag Your Files here that are for the assignment - References, PDFs, Maya, zip files etc, you can queue more than one. They will upload when you create the assignment.'"></i><br />
                                <dropzone id="assignmentFiles" ref="assignmentFiles" url="#" v-bind:use-custom-dropzone-options="true" v-bind:thumbnail-height="25" v-bind:dropzone-options="dropzoneOptions">
                                </dropzone>
                                <div class="form-group">
                                    <i class="clippie clippie-float-right fa fa-info-circle" v-tooltip="'An assignment image is the featured image, it shows in the description as a popup and also as the assignment background! Usually its for the final result etc.'"></i><label>Assignment Featured Image</label>
                                    <input type="file" class="form-control" ref="assignmentImage">
                                </div>
                                <div class="form-group">
                                    <input type="submit" class="btn btn-success" value="Create Assignment">
                                </div>
                            </form>
                        </div>
                        <div class="text-right" v-if="!video.assignment">
                            <div class="checkbox-wrapper" @click.prevent="toggleRequirement('assignment')">
                                There is no assignment this week
                                <i class="fa fa-square-o" v-if="requires('assignment')"></i>
                                <i class="fa fa-check-square-o" v-if="!requires('assignment')"></i>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="panel exercise" v-if="selectedTab==='exercise'">
                    <div class="panel-heading">
                        <h2>Exercise</h2>
                    </div>
                    <div class="panel-body">
                        <div v-if="requires('exercise')">
                            <h4 v-if="!video.exercise" class="create-assignment-title">Create a New Exercise</h4>
                            <p v-if="!video.exercise">Below you can add an Exercise to this week's lesson in this course.<br /><br /></p>
                            <div v-if="video && video.exercise">
                                <form>
                                    <div class="form-group">
                                        <h5>Name</h5>
                                        <input v-model="video.exercise.title" class="form-control">
                                    </div>
                                    <div class="form-group">
                                        <i class="clippie clippie-float-right fa fa-info-circle" v-tooltip="'Set the date the exercise is due.'"></i>
                                        <label>Due Date</label>
                                        <input class="form-control" type="date" v-model="video.exercise.dueDate">
                                    </div>
                                    <div class="form-group">
                                        <h5>Description:</h5>
                                        <vue-html5-editor @change="changeExerciseDescription" :content="video.exercise.description" :height="200"></vue-html5-editor>
                                    </div>
                                    <div class="form-group">
                                        <h5>Exercise Image</h5>
                                        <img v-if="video.exercise.image" :src="video.exercise.image">
                                        <input class="form-control" type="file" @change="changeExerciseImage($event)">
                                    </div>
                                    <div class="form-group">
                                        <h5>Files</h5>
                                        <h6 v-if="video.exercise.watermarkingInProgress">Watermarking in progress</h6>
                                        <table class="assignment-files table table-striped ">
                                            <tbody>
                                                <tr v-for="(file, index) in video.exercise.files" class="list-group-item">
                                                    <td><a :href="file" _target="blank">{{file | filenameFromUrl}}</a></td>
                                                    <td v-if="!video.exercise.watermarkingInProgress"><button @click.prevent="deleteFile('exercise', index)" class="btn btn-xs btn-danger">Delete</button></td>
                                                    <td v-if="!video.exercise.watermarkingInProgress">
                                                        <button class="btn btn-xs btn-success" @click.prevent="protectFile('exercise', video.exercise, file)">
                                                            Protect</button></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                        <div class="form-group">
                                            <input class="form-control" type="file" @change="uploadExerciseFiles($event)">
                                        </div>
                                        <h5>Watermarked Files</h5>
                                        <table class="assignment-files table table-striped ">
                                            <tbody>
                                                <tr v-for="(file, index) in video.exercise.watermarkedFiles" class="list-group-item">
                                                    <td>
                                                        <a @click.prevent="downloadFile(file, 'exercise', video.exercise.id)" href="#">{{file | filenameFromUrl}}</a>
                                                        <span v-if="isDirector">(<a @click.prevent="downloadFile(file, 'exercise', video.exercise.id, true)" href="#">source</a>)</span>
                                                    </td>
                                                    <td><button @click.prevent="deleteWatermarkedFile(file, 'exercise', video.exercise.id)" class="btn btn-xs btn-danger">Delete</button></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div class="form-group row">
                                        <label><input class="form-control" type="checkbox" v-model="video.exercise.fileRequired">File upload required</label>
                                    </div>
                                    <div class="form-group buttons">
                                        <div class="col-sm-6">
                                            <button @click.prevent="updateExercise()" class="btn btn-success">Update</button>
                                        </div>
                                        <div class="col-sm-6 text-right">
                                            <button class="btn btn-danger" @click.prevent="deleteExercise()">Delete</button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                            <form v-else @submit.prevent="createExercise()">
                                <div class="form-group">
                                    <i class="clippie clippie-float-right fa fa-info-circle" v-tooltip="'Enter exercise name on white line.'"></i>
                                    <input class="form-control" placeholder="Title" v-model="exercise.title">
                                </div>
                                <div class="form-group">
                                    <i class="clippie clippie-float-right fa fa-info-circle" v-tooltip="'Set the date the exercise is due.'"></i>
                                    <label>Due Date</label>
                                    <input class="form-control" type="date" v-model="exercise.dueDate">
                                </div>
                                <div class="form-group">
                                    <i class="clippie clippie-float-right fa fa-info-circle" v-tooltip="'Fill out exercise details and requirements, you can use bullets, bolds etc for better formatting.'"></i>
                                    <vue-html5-editor @change="updateExerciseDescription" :content="exerciseDescription" :height="200"></vue-html5-editor>
                                </div>
                                <div class="form-group clearfix">
                                    <i class="clippie clippie-float-right fa fa-info-circle" v-tooltip="'Check Hand-In File Required if they need to submit something and not just self assess.'"></i><label>Hand-in File Required? <input type="checkbox" class="form-control" v-model="exercise.fileRequired"></label>
                                </div>
                                <div class="form-group">
                                    <i class="clippie clippie-float-right fa fa-info-circle" v-tooltip="'An exercise image is the featured image, it shows in the description as a popup and also as the exercise background! Usually its for the final result etc.'"></i><label>Exercise Featured Image:</label>
                                    <input type="file" ref="exerciseImage" class="form-control exercise-image-upload">
                                </div>
                                <i class="clippie clippie-float-right fa fa-info-circle" v-tooltip="'Drag Your Files here that are for the excerise - References, PDFs, Maya, zip files etc, you can queue more than one. They will upload when you create the excerise.'"></i><br />
                                <dropzone id="exerciseFiles" ref="exerciseFiles" url="#" v-bind:use-custom-dropzone-options="true" v-bind:thumbnail-height="0" v-bind:dropzone-options="dropzoneOptions">
                                    <input type="hidden" name="token" value="xxx">
                                </dropzone>
                                <div class="form-group">
                                    <button type="submit" class="btn btn-success">Create Exercise</button>
                                </div>
                            </form>
                        </div>
                        <div class="text-right">
                            <div class="checkbox-wrapper" @click.prevent="toggleRequirement('exercise')" v-if="!video.exercise">
                                There is no exercise this week
                                <i class="fa fa-square-o" v-if="requires('exercise')"></i>
                                <i class="fa fa-check-square-o" v-if="!requires('exercise')"></i>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="panel" v-if="selectedTab==='comments'">
                    <div class="panel-heading">
                        <h2>Comments</h2>
                    </div>
                    <div class="panel-body">
                        <ul class="comments" v-if="comments">
                            <li v-for="comment in comments">
                                <div class="row top-level">
                                    <div class="col-xs-2">
                                        <img v-if="comment.user.avatar" v-bind:src="comment.user.avatar">
                                    </div>
                                    <div class="col-xs-7">
                                        <p class="author">
                                            {{comment.user.firstName}} {{comment.user.lastName}} at <span @click="jumpToTime(comment.time)">{{comment.time | minutesAndSeconds}}</span>
                                        </p>
                                        <div v-html="comment.text"></div>
                                    </div>
                                    <div class="col-xs-3">
                                        <div>{{comment.createdAt | timeSince}} ago</div>
                                        <div>
                                            <button class="btn btn-xs reply-button" @click.prevent="showReplyForm = comment.id">Reply</button>
                                            <button class="btn btn-xs delete-button" @click.prevent="deleteComment(comment)" v-if="localStorage.roles.includes('CourseAdmin') || localStorage.roles.includes('Admin')">
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <ul class="replies" v-if="comment.children.length > 0">
                                    <li v-for="child in comment.children">
                                        <div class="row">
                                            <div class="col-xs-2">
                                                <img v-if="child.user.avatar" v-bind:src="child.user.avatar">
                                            </div>
                                            <div class="col-xs-7">
                                                <p class="author">
                                                    {{child.user.firstName}} {{child.user.lastName}} at <span @click="jumpToTime(comment.time)">{{comment.time | minutesAndSeconds}}</span>
                                                </p>
                                                <div v-html="child.text"></div>
                                                </p>
                                            </div>
                                            <div class="col-xs-3">
                                                <div>{{child.createdAt | timeSince}} ago</div>
                                                <button class="btn btn-xs delete-button" @click.prevent="deleteComment(child)" v-if="localStorage.roles.includes('CourseAdmin') || localStorage.roles.includes('Admin')">
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                                <form v-if="showReplyForm===comment.id" @submit.prevent="addComment(comment.id, comment.user)">
                                    <div class="form-group">
                                        <vue-html5-editor @change="updateCommentReply($event, comment.id)" :content="content" :height="200"></vue-html5-editor>
                                    </div>
                                    <button type="submit" class="btn btn-xs reply-button">Submit Comment Reply</button>
                                </form>
                            </li>
                        </ul>
                        <form @submit.prevent="addComment()">
                            <div class="form-group">
                                <label>Add Timeline Comment:<i class="clippie fa fa-info-circle" v-tooltip="'Add and reply to comments directly on the timeline. Great for asking and answering questions specific to a point in time. Set your time marker to a place and add a comment!'"></i></label>
                                <vue-html5-editor @change="updateComment" :content="content" :height="200"></vue-html5-editor>
                            </div>
                            <button type="submit" class="btn btn-success">Add Comment</button>
                        </form>
                    </div>
                </div>
                <div class="panel" v-if="selectedTab==='resources'">
                    <div class="panel-heading">
                        <h2>Course Resources</h2>
                    </div>
                    <div class="panel-body resources">
                        <div v-if="requires('resources')">
                            <div class="resource-table">
                                <ul>
                                    <li v-for="(resource, index) in video.resources">
                                        <a :href="resource.url" target="_blank">{{resource.name}}</a>
                                        <p>{{resource.description}}</p>
                                        <p v-if="resource.watermarkingInProgress">Watermarking in progress</p>
                                        <div class="buttons" v-if="!resource.watermarked">
                                            <button class="btn-xs btn-danger" @click.prevent="deleteResource(resource, index)">
                                                Delete
                                            </button>
                                            <button @click.prevent="protectFile('resource', resource, resource.url)" v-if="!resource.watermarkingInProgress && resource.url.includes('ttto-course-files')" class="btn-xs btn-success">
                                                Protect
                                            </button>
                                            <span v-if="resource.watermarkingInProgress">Watermarking in progress</span>
                                        </div>
                                        <div v-else class="buttons">
                                            <button class="btn-xs btn-danger" @click.prevent="deleteWatermarkedFile(resource.url, 'resource', resource.id)">
                                                Delete
                                            </button>
                                            <button @click.prevent class="btn btn-primary">Protected</button>
                                        </div>
                                    </li>
                                </ul>
                                <!-- <table class="table">
                                    <tbody>
                                        <tr v-for="(resource, index) in video.resources">
                                            <td><i @click.prevent="deleteResource(resource.id, index)" class="fa fa-remove"></i></td>
                                            <td class="resource-week">Week {{resource.week}}</td>
                                            <td class="resource-description">{{resource.description}}</td>
                                            <td class="resource-name"><a :href="resource.url" target="_blank">{{resource.name}}</a></td>
                                        </tr>
                                    </tbody>
                                </table> -->
                            </div>
                            <p>Here you can add resources for this course, tied to this week. Name and Description are required and either the resource would be an external URL like to a website, or you can add a file, be it a zip, PDF, image etc!</p>
                            <p>They also will be in the Course Resource Directory in the left menu.</p>
                            <file-list v-if="course" :instanceId="course.instanceid"></file-list>
                            <form @submit.prevent="uploadResource()">
                                <div class="form-group">
                                    <input class="form-control" type="text" v-model="file.name" placeholder="Name">
                                </div>
                                <div class="form-group">
                                    <input class="form-control" type="text" v-model="file.description" placeholder="Description">
                                </div>
                                <div class="form-group">
                                    <input class="form-control" type="text" v-model="file.url" placeholder="URL">
                                </div>
                                <div class="form-group">
                                    <label>Or upload file:</label>
                                    <input class="form-control" ref="file" type="file" name="file">
                                </div>
                                <div class="form-group">
                                    <button type="submit" class="btn btn-success">Add Course Resource</button>
                                </div>
                            </form>
                        </div>
                        <div class="text-right">
                            <div class="checkbox-wrapper" @click.prevent="toggleRequirement('resources')" v-if="video.resources.length < 1">
                                There are no resources this week
                                <i class="fa fa-square-o" v-if="requires('resources')"></i>
                                <i class="fa fa-check-square-o" v-if="!requires('resources')"></i>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="panel annotation" v-if="selectedTab==='transcript'">
                    <div class="panel-heading">
                        <h2>Annotations &#38; Flyouts</h2>
                    </div>
                    <div class="panel-body" v-if="selectedChapter">
                        <p>Annotations and Flyouts are a great way to overlay additional captions to your videos. Even just text like a caption for further information. Or you could say "Tumble Viewport" and set the shortcut below. </p>
                        <p>First drag the timeline dot to set the place in the video lesson you'd like to add the annoation.</p>
                        <p>You can edit and delete annotations by finding them in the timeline and clicking either the delete icon or pencil icon to edit them.</p>
                        <p>Shortcuts, modifier keys, mouse clicks are entirely optional.</p>
                        <p>Left and Right is which location the fly-out appears over the video.</p>
                        <form>
                            <div class="form-group" v-if="!editMode">
                                <h4><br />Add Annotation at
                                    <input @keyup="jumpToTimecode($event)" ref="timecode" class="timecode form-control" type="text" :value="currentTime | timecode"></h4>
                            </div>
                            <div v-else class="form-group">Editing annotation at {{annotation.time}}</div>
                            <textarea v-model="annotation.text" placeholder="Text" class="form-control"></textarea>
                            <div class="buttons">
                                <input class="key" type="checkbox" value="ctrl" id="ctrl" v-model="controlKeys"><label for="ctrl" class="key">CTRL</label>
                                <input class="key" type="checkbox" value="alt" id="alt" v-model="controlKeys"><label for="alt" class="key">ALT</label>
                                <input class="key" type="checkbox" value="shift" id="shift" v-model="controlKeys"><label for="shift" class="key">SHIFT</label>
                                <input class="key" type="checkbox" value="lmb" id="lmb" v-model="mouseButtons"><label for="lmb" class="mouse"></label>
                                <input class="key" type="checkbox" value="cmb" id="cmb" v-model="mouseButtons"><label for="cmb" class="mouse"></label>
                                <input class="key" type="checkbox" value="rmb" id="rmb" v-model="mouseButtons"><label for="rmb" class="mouse"></label>
                                <input type="text" class="hotkey form-control" placeholder="Key" v-model="hotKeys">
                                <input type="radio" v-model="annotation.position" value="left">Left
                                <input type="radio" v-model="annotation.position" value="right">Right
                                <input v-if="editMode===false" @click.prevent="addAnnotation" type="submit" class="btn btn-success" value="Add Annotation">
                                <input v-else @click.prevent="updateAnnotation" type="submit" class="btn btn-success" value="Update">
                            </div>
                        </form>
                        <table class="annotations">
                            <thead>
                                <tr>
                                    <th>Timecode</th>
                                    <th>Annotation</th>
                                    <th>Edit</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="annotation in selectedChapter.annotations">
                                    <td>{{annotation.time | timecode}}</td>
                                    <td>{{annotation.text}}</td>
                                    <td>
                                        <i @click="editAnnotation(annotation.id)" class="fa fa-pencil"></i>
                                        <i @click="deleteAnnotation(annotation.id)" class="fa fa-trash-o"></i>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div v-else>Please select a chapter</div>
                </div>
                <div class="panel" v-if="selectedTab==='transcript'">
                    <div class="panel-heading">
                        <h2>Transcript Editor</h2>
                    </div>
                    <div class="panel-body" v-if="selectedChapter">
                        <p>You can review the select chapter's video transcript below.
                            (<a v-if="video.video_chapters && video.video_chapters.length" href="#" class="bold underline" @click.prevent="copyTranscript()">Click to copy all lesson chapter transcripts</a>)
                        </p>
                        <p>They sometimes take up to an hour to generate but will appear once ready!</p>
                        <p>You can edit them directly below and save your changes if they need improving.</p>
                        <div class="transcript" v-if="selectedChapter && selectedChapter.transcript">
                            <div class="text well" ref="text">
                                <span @keyup="updateText($event, index)" contenteditable="true" v-for="(phrase,index) in selectedChapter.transcript.text">{{phrase.text | nobreak}} </span>
                            </div>
                            <div class="form-group">
                                <button @click.prevent="updateTranscript" class="btn btn-primary">Save Transcript Edits</button>
                            </div>
                        </div>
                        <button v-if="selectedChapter" @click.prevent="regenerateTranscript()" class="btn btn-default">Regenerate Transcript</button>
                    </div>
                </div>
            </div>
            <div class="panel chapters-panel col-xs-12 col-md-4">
                <div class="panel-heading">
                    <h2>Chapters</h2>
                </div>
                <div class="panel-body">
                    <draggable v-model="video.video_chapters" @end="reorder($event)" tag="ul" class="chapters list-group" handle=".handle">
                        <li v-for="(chapter, index) in video.video_chapters" class="row">
                            <div class="col-xs-12 chapter-heading form-group" v-if="chapter.heading">
                                <input type="text" v-model="chapter.heading" class="form-control">
                            </div>
                            <div class="col-xs-7">
                                <input @click="selectChapter(chapter)" class="form-control" v-model="chapter.title" type="text" :class="{'selected':selectedChapter.id === chapter.id}">
                            </div>
                            <div class="col-xs-5 buttons">
                                <i class="fa fa-save" @click="rename(chapter, chapter.id)"></i>
                                <!-- <label><i class="fa fa-upload"></i><input type="file" @change="replaceVideo($event, index, chapter.id)"></label> -->
                                <i class="fa fa-copy" title="Copy chapter title and ID to clipboard" @click="copyChapter(chapter)"></i>
                                <i class="fa fa-trash" @click.prevent="deleteChapter(chapter.id, index)"></i>
                                <span @click="selectChapter(chapter)" class="duration">{{chapter.duration | minutesAndSeconds}}</span>
                                <i class="fa fa-arrows-v handle" title="Drag to re-order"></i>
                            </div>
                        </li>
                    </draggable>
                    <dropzone id="newChapters" ref="newChapters" url="#" v-on:vdropzone-file-added="chapterAdded" v-bind:use-custom-dropzone-options="true" v-bind:thumbnail-height="0" v-bind:dropzone-options="dropzoneOptions">
                        <input type="hidden" name="token" value="xxx">
                    </dropzone>
                    <draggable v-model="chapters" class="chapters list-group">
                        <li v-for="chapter in chapters" class="row">
                            <div class="col-xs-12">
                                <input v-model="chapter.title" type="text" class="form-control" placeholder="Title">
                            </div>
                            <div class="col-xs-12">
                                <input v-model="chapter.heading" type="text" class="form-control" placeholder="Group Heading (optional)">
                            </div>
                            <div class="col-xs-12">
                                {{chapter.name}}
                            </div>
                        </li>
                    </draggable>
                    <div class="form-group upload text-right">
                        <button @click.prevent="addChapters" class="btn btn-default">Upload</button>
                    </div>
                </div>
                <div class="panel-heading">
                    <h2>Weekly Summary</h2>
                </div>
                <div class="panel-body summary">
                    <p>Weekly summaries populate the course home table, they should only be max a few paragraphs in length. Excerpts of the 'lesson description' for each week. These need to be completed ahead of time as future summaries are visible to students.</p>
                    <div>
                        <form @submit.prevent="saveDescription()">
                            <textarea class="form-control" v-model="video.summary"></textarea>
                            <input type="submit" class="btn btn-success" value="Save">
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import videos from '../services/videos';
import aws from '../services/aws';
import files from '../services/files';
import exercises from '../services/exercises';
import assignments from '../services/assignments';
import paths from '../services/paths';
import terms from '../services/terms';

import S3Utils from '../packages/s3-utils';

import fileProgress from './reusable/file-progress/FileProgress.vue';
import fileList from './reusable/files/Files.vue';
import PolyVideo from './reusable/poly-video';
import Resources from './widgets/Resources.vue';
import videoJsResolutionSwitcher from '../packages/videojs-resolution-switcher';
import breadcrumbs from './widgets/ForumBreadcrumbs.vue';

import moment from 'moment';
import Dropzone from 'vue2-dropzone';
import draggable from 'vuedraggable';
import overlay from 'videojs-overlay';
import toastr from 'toastr';
require('../packages/videojs-markers');

const uuidV4 = require('uuid/v4');

export default {
    data() {
        return {
            selectedTab: 'description',
            syncedLessons: [],
            allPaths: [],
            assignment: {},
            assignmentFiles: [],
            assignmentImage: null,
            exerciseFiles: [],
            lessonFilesToUpload: [],
            instanceId: null,
            videoId: null,
            video: null,
            videoTerm: null,
            currentTime: null,
            timecode: null,
            comments: null,
            selectedComment: null,
            showReplyForm: null,
            newComment: null,
            commentReply: {},
            transcriptChanges: [],
            chapters: [],
            chaptersToAdd: [],
            selectedChapter: null,
            exerciseDescription: '',
            videoDescription: '',
            unsyncedSKUs: ['na', 'n/a', 'NA', 'N/A', 'extra_submission_week'],
            file: {},
            dropzoneOptions: {
                autoProcessQueue: false,
                addRemoveLinks: true,
            },
            exercise: {
                fileRequired: true
            },
            isMultiWeek: false,
            controlKeys: [],
            mouseButtons: [],
            annotation: {
                text: '',
                hotKeys: [],
                time: null,
                position: 'left'
            },
            annotations: [],
            editMode: false,
            videoPlayerUrl: null,
            videoPlayerVjsOptions: {
                playbackRates: [0.7, 1.0, 1.25, 1.5, 2.0, 3.0],
            },
        }
    },
    components: {
        'file-list': fileList,
        Resources,
        Dropzone,
        draggable,
        breadcrumbs,
        PolyVideo,
        fileProgress,
    },
    mounted() {

        var self = this;
        this.courseId = this.$route.params.courseId;
        this.instanceId = this.$route.params.instanceId;
        this.videoId = this.$route.params.videoId;
        this.path = this.$route.query.path;
        this.term = this.$route.query.term;

        this.getVideo();
        this.getSyncedLessons();
        paths.getPaths(false).then((response) => {
            this.allPaths = response;
        });
        terms.get(this).then((response) => {
            this.videoTerm = response.data.terms.find(t => t.name === this.term);
        });

        this.sockets.subscribe('watermarking_finished', ({ type, id, url }) => {
            toastr.clear();
            toastr.success('File protected')
            switch (type) {
                case 'assignment':
                    if (this.video.assignment && this.video.assignment.id === id) {
                        this.video.assignment.watermarkingInProgress = false;
                        this.getVideo();
                    }


                    break;

                case 'exercise':
                    if (this.video.exercise && this.video.exercise.id === id) {
                        this.video.exercise.watermarkingInProgress = false;
                        this.getVideo();
                    }
                    break;

                case 'resource':
                    if (this.video.resources) {
                        const resource = this.video.resources.find(r => r.id === id);
                        resource.watermarkingInProgress = false;
                        this.getVideo();
                    }
                    break;
            }
        });

    },
    methods: {
        getPathForCourse(courseId) {
            const path = this.allPaths.find((p) => {
                return p.courses.includes(courseId)
            });
            if (path) {
                return path.name;
            } else {
                return '';
            }
        },
        getSyncedLessons() {
            videos.getSyncedLessons(this.videoId).then((response) => {
                this.syncedLessons = response.data.syncedLessons || [];
            });
        },
        copyTranscript() {
            const transcriptText = this.video.video_chapters.map((c) => {

                if (!c.transcript) { return ''; }

                if (!c.transcript.text) { return c.title + '\n\n'; }

                const text = c.transcript.text.map(t => t.text).join(' ');

                return c.title + '\n\n' + text;

            }).join('\n\n\n');

            navigator.clipboard.writeText(transcriptText);
            toastr.success('Copied to clipboard')


        },
        openStudentView() {
            let url = `https://${window.location.host}/#/videos/${this.courseId}/${this.instanceId}/${this.videoId}`;
            url += '?week=' + this.video.week
            window.open(url, '_blank');


        },
        downloadFile(file, type, id, source) {
            this.$store.dispatch('populate', {
                type: 'downloading',
                data: true
            });
            this.$bus.$emit('download', file, type, id, source);

        },
        protectFile(type, record, file) {
            const ext = file.split('?')[0].split('.').pop();

            if (ext !== 'zip') {
                toastr.error('Only zip files can be watermarked');
                return;
            }


            toastr.options.timeOut = 0;
            toastr.options.extendedTimeOut = 0;
            files.protect(type, record, file).then((response) => {
                if (response.data.success === true) {
                    toastr.info(response.data.message);
                    this.getVideo();
                } else {
                    toastr.error(response.data.message);
                }
            });

        },
        isWatermarked(files, file) {
            if (!files) files = [];
            return files.includes(file);
        },
        async deleteComment(comment) {
            const yes = window.confirm('Are you sure you want to delete this comment?');
            if (!yes) {
                return;
            }
            await videos.deleteComment(comment.id);
            if (comment.children && comment.children.length) {
                for (const comment of comment.children) {
                    await videos.deleteComment(comment.id);
                }
            }
            toastr.success('Comment deleted');
            this.getComments();
        },
        updateComment(content) {
            this.newComment = content;
        },

        updateCommentReply(content, id) {
            this.commentReply[id] = content;
        },

        jumpToTimecode(e) {

            if (e.keyCode == 8 || e.keyCode == 46) return; // deleting

            var self = this;
            let timecode = this.$refs.timecode.value;

            var player = self.$refs.videoPlayer.getVjsPlayer();
            try {
                var a = timecode.split(':');
                var seconds = (+a[0]) * 60 * 60 + (+a[1]) * 60 + (+a[2]);
                self.currentTime = seconds;
                player.currentTime(seconds)
            } catch (e) {
                toastr.error('Invalid timecode');
            }
        },
        deleteAssignment(id) {
            var self = this;
            assignments.delete(this, id).then(function(response) {
                if (response.data.success === true) {
                    toastr.success('Assignment deleted');
                    self.video.assignment = null;
                } else {
                    toastr.error('Error deleting assignment');
                }
            });
        },
        addAssignment() {
            var self = this;
            self.$store.dispatch('toggle', { type: 'loading', boolean: true });


            const illegal = /[*?:;<>+&!"|\\/]/;

            if (illegal.test(this.assignment.name)) {
                toastr.error('Plese remove non-alphanumeric characters from assignment name');
                self.$store.dispatch('toggle', { type: 'loading', boolean: false });
                return false;
            }

            if (this.isMultiWeek === true && !this.assignment.duration) {
                toastr.error('Please select the number of weeks');
                self.$store.dispatch('toggle', { type: 'loading', boolean: false });
                return false;
            }
            if ((parseInt(this.assignment.duration) + parseInt(this.video.week)) > 14) {
                toastr.error('Due date cannot be after the semester has ended');
                self.$store.dispatch('toggle', { type: 'loading', boolean: false });
                return false;
            }

            self.uploadAssignmentFiles().then(function(done) {
                self.addAssignmentImage().then(function(d) {
                    self.createAssignment();

                }).catch(function(e) {
                    self.$store.dispatch('toggle', { type: 'loading', boolean: false });
                })
            }).catch(function(e) {
                self.$store.dispatch('toggle', { type: 'loading', boolean: false });
            })

        },
        deleteFile(type, index) {
            if (type === 'assignment') {
                this.video.assignment.files.splice(index, 1);
                this.updateAssignment();
            } else if (type === 'exercise') {
                this.video.exercise.files.splice(index, 1);
                this.updateExercise();

            }
        },
        deleteWatermarkedFile(file, type, id) {
            files.deleteWatermarked(file, type, id).then((response) => {
                response.data.success === true ? toastr.success('File deleted') : toastr.error('Error deleting file');
                this.getVideo();
            });
        },
        updateAssignment() {
            var self = this;

            const illegal = /[*?<:;>+&!"|\\/]/;

            if (illegal.test(this.video.assignment.name)) {
                toastr.error('Plese remove non-alphanumeric characters from assignment name');
                self.$store.dispatch('toggle', { type: 'loading', boolean: false });
                return false;
            }

            let update = {
                id: this.video.assignment.id,
                name: this.video.assignment.name.trim(),
                difficulty: this.video.assignment.difficulty,
                points: this.video.assignment.points,
                description: this.video.assignment.description,
                image: this.video.assignment.image,
                files: this.video.assignment.files,
                isFinalProject: this.video.assignment.isFinalProject,
                dueDate: moment(this.video.assignment.dueDate).endOf('day').tz('America/Vancouver').toDate(),
                availableDate: moment(this.video.assignment.availableDate).tz('America/Vancouver').toDate(),
            }


            assignments.update(this, update).then(function(response) {
                response.data.success === true ? toastr.success('Updated') : toastr.error('Error updating.');
            });
        },
        changeAssignmentImage(event) {
            var self = this;
            this.addAssignmentImage(event.target.files[0]).then(function(url) {
                self.video.assignment.image = url;
            });
        },
        addAssignmentImage(file) {

            var self = this;

            if (!file && this.$refs.assignmentImage.files.length < 1) {
                return new Promise(function(resolve, reject) {
                    resolve(true);
                });
            }
            if (!file) {
                var file = this.$refs.assignmentImage.files[0];
            }
            var filename = file.name.split('.')[0] + '_' + new Date().getMilliseconds() + parseInt(Math.random() * 100) + '.' + file.name.split('.').pop();
            filename = filename.replace(/&|\$|@|=|;|:|\%|\+|,|\?/g, '');

            return new Promise(function(resolve, reject) {
                aws.signature(file, filename, 'ttto-assignment-files').then(function(signature) {
                    aws.getPresignedUrl(self, 'ttto-assignment-files', filename, 'putObject').then(function(presignedUrl) {
                        aws.upload(file, filename, signature, 'ttto-assignment-files', presignedUrl.data).then(function(response) {
                            self.assignmentImage = response.url;
                            resolve(response.url)
                        }).catch(function(error) {
                            console.log(error);
                            resolve(false)
                        });
                    });
                });
            });

        },

        async uploadAssignmentFiles(event) {
            if (!event && this.$refs.assignmentFiles.dropzone.files.length < 1) {
                return true;
            }

            let files;
            if (!event) {
                files = this.$refs.assignmentFiles.dropzone.files;
            } else {
                files = [...event.target.files];
            }

            let valid = true;

            for (let file of files) {
                if (file.name.split('.').pop() !== 'zip') {
                    valid = false;
                }
            }

            if (valid === false) {
                toastr.error('Please zip your assets before uploading them');
                return;
            }

            try {
                await Promise.all(
                    files.map(async file => {
                        const name = file.name.split('.')[0];
                        const ext = file.name.split('.').pop();
                        const time = new Date().getMilliseconds();
                        const filename = `${name}_${time}.${ext}`;
                        const bucket = 'ttto-assignment-files';
                        let res;
                        const sig = await aws.signature(file, filename, bucket);
                        res = await aws.getPresignedUrl(this, bucket, filename, 'putObject');
                        res = await aws.upload(file, filename, sig, bucket, res.data);
                        res = await aws.getPresignedUrl(this, bucket, filename, 'getObject')

                        if (this.video && this.video.assignment) {
                            if (this.video.assignment.files) {
                                this.video.assignment.files.push(res.data);
                            } else {
                                this.video.assignment.files = [res.data];
                            }
                            this.updateAssignment();
                            this.getVideo();
                        } else {
                            this.assignmentFiles.push(res.data);
                        }
                    })
                );
                return true;

            } catch (error) {
                console.error('failed to upload files:', error);
                return false;
            }
        },

        createAssignment() {
            var self = this;

            var data = {
                courseId: this.courseId,
                instanceId: this.instanceId,
                name: this.assignment.name.replace(/\+/g, 'and').trim(),
                description: this.assignment.description,
                difficulty: this.assignment.difficulty,
                points: this.assignment.points,
                videoId: this.video.id,
                week: this.video.week,
                isFinalProject: this.assignment.isFinalProject,
                duration: this.assignment.duration || 1
            }


            if (this.assignmentFiles.length > 0) {
                data.files = this.assignmentFiles;
            }
            if (this.assignmentImage) {
                data.image = this.assignmentImage;
            }

            assignments.create(this, data).then(function(response) {
                if (response.data.success === true) {
                    toastr.success('Assignment created');
                    response.data.assignment.availableDate = moment(response.data.assignment.availableDate).tz('America/Vancouver').format('YYYY-MM-DD');
                    response.data.assignment.dueDate = moment(response.data.assignment.dueDate).tz('America/Vancouver').format('YYYY-MM-DD');
                    self.video.assignment = response.data.assignment;

                    self.assignmentFiles = [];
                    self.assignmentImage = null;
                    self.assignment = {};

                    self.$store.dispatch('toggle', { type: 'loading', boolean: false });

                } else {
                    toastr.error(response.data.message || 'Error creating assignment');
                    self.$store.dispatch('toggle', { type: 'loading', boolean: false });

                }
            });

        },
        toggleRequirement(type) {
            let current = this.video.doesNotRequire.indexOf(type);
            let toggle;
            if (current > -1) {
                toggle = false;
                this.video.doesNotRequire.splice(current, 1);
            } else {
                toggle = true;
                this.video.doesNotRequire.push(type);
            }
            let video = {};
            video.doesNotRequire = this.video.doesNotRequire;
            video.id = this.video.id;
            videos.updateVideo(this, video);

        },
        requires(type) {
            return this.video.doesNotRequire.indexOf(type) === -1;
        },

        deleteLessonFile(id, index) {
            var self = this;
            videos.deleteLessonFile(this, id).then(function(response) {
                if (response.data.success === true) {
                    self.video.lesson_files.splice(index, 1);
                }
            })
        },
        addLessonFiles() {
            var self = this;
            this.uploadLessonFiles().then(function(done) {
                var data = {
                    videoId: self.video.id,
                    urls: self.lessonFilesToUpload
                }
                videos.addLessonFiles(self, data).then(function(response) {
                    self.getVideo();
                });
            });
        },

        async uploadLessonFiles() {
            if (this.$refs.lessonFiles.dropzone.files.length < 1) {
                return false;
            }

            if (this.uploading) {
                toastr.error('You are already uploading a file');
                return false;
            }

            const files = this.$refs.lessonFiles.dropzone.files;

            try {
                this.uploading = true;

                await Promise.all(
                    files.map(async file => {
                        const name = file.name.split('.')[0];
                        const ext = file.name.split('.').pop();
                        const time = new Date().getMilliseconds();
                        const filename = `${name}_${time}.${ext}`;
                        const bucket = 'ttto-lesson-files';

                        let res;
                        const sig = await aws.signature(file, filename, bucket);
                        res = await aws.getPresignedUrl(this, bucket, filename, 'putObject');
                        res = await aws.upload(file, filename, sig, bucket, res.data);
                        res = await aws.getPresignedUrl(this, bucket, filename, 'getObject')

                        this.lessonFilesToUpload.push(res.data);
                    })
                );

                return true;

            } catch (error) {
                console.error('failed to upload files:', error);
                return false;

            } finally {
                this.uploading = false;
            }
        },
        updateVideoDescription(content) {
            this.videoDescription = content;
        },
        updateExerciseDescription(content) {
            this.exerciseDescription = content;
        },
        updateAssignmentDescription(content) {
            this.assignment.description = content;
        },
        changeAssignmentDescription(content) {
            this.video.assignment.description = content;
        },
        changeExerciseDescription(content) {
            this.video.exercise.description = content;
        },
        saveDescription() {
            this.video.description = this.videoDescription;
            videos.updateVideo(this, this.video).then(function(response) {
                if (response.data.success === true) {
                    toastr.success('Updated');
                } else {
                    toastr.error('Error updating');
                }
            });
        },
        selectChapter(chapter) {

            this.videoPlayerUrl = S3Utils.buildS3Url(
                'ttto-course-videos-raw',
                `${chapter.url}.mp4`,
            );
            this.selectedChapter = chapter;
        },
        deleteExercise() {
            var self = this;
            exercises.delete(this, this.video.exercise.id).then(function(response) {
                if (response.data.success === true) {
                    toastr.success('Deleted');
                    self.video.exercise = null;

                } else {
                    toastr.error('Error deleting exercise')
                }
            })
        },
        async uploadExerciseFiles(event) {

            if (!event && this.$refs.exerciseFiles.dropzone.files.length < 1) {
                return true;
            }

            if (this.uploading) {
                toastr.error('You are already uploading a file');
                return false;
            }

            let files;
            if (!event) {
                files = this.$refs.exerciseFiles.dropzone.files;
            } else {
                files = [...event.target.files];
            }

            let valid = true;

            for (let file of files) {
                if (file.name.split('.').pop() !== 'zip') {
                    valid = false;
                }
            }

            if (valid === false) {
                toastr.error('Please zip your assets before uploading them');
                return;
            }
            try {
                this.uploading = true;

                await Promise.all(
                    files.map(async file => {
                        const name = file.name.split('.')[0];
                        const ext = file.name.split('.').pop();
                        const time = new Date().getMilliseconds();
                        const filename = `${name}_${time}.${ext}`;
                        const bucket = 'ttto-lesson-files';
                        let res;
                        const sig = await aws.signature(file, filename, bucket);
                        res = await aws.getPresignedUrl(this, bucket, filename, 'putObject');
                        res = await aws.upload(file, filename, sig, bucket, res.data);
                        res = await aws.getPresignedUrl(this, bucket, filename, 'getObject')

                        if (this.video && this.video.exercise) {
                            if (this.video.exercise.files) {
                                this.video.exercise.files.push(res.data);
                            } else {
                                this.video.exercise.files = [res.data];
                            }
                            this.updateExercise();
                        } else {
                            this.exerciseFiles.push(res.data);
                        }
                    })
                );

                return true;

            } catch (error) {
                console.error('failed to upload files:', error);
                return false;

            } finally {
                this.uploading = false;
            }
        },
        changeExerciseImage(event) {
            var self = this;
            this.uploadExerciseImage(event).then(function(done) {

            });
        },

        updateExercise() {
            var self = this;
            var update = {
                id: self.video.exercise.id,
                title: self.video.exercise.title,
                image: self.video.exercise.image,
                description: self.video.exercise.description,
                files: self.video.exercise.files,
                dueDate: self.video.exercise.dueDate,
                fileRequired: self.video.exercise.fileRequired
            }

            exercises.update(this, update).then(function(response) {
                response.data.success === true ? toastr.success('Updated') : toastr.error('Error updating');
            });
        },
        createExercise() {
            var self = this;

            if (!this.exerciseDescription || !this.exercise.title) {
                toastr.error('Please add a title and description');
                return;
            }
            if (!self.video) {
                toastr.error('Please add a title and description');
                return;
            }
            self.$store.dispatch('toggle', { type: 'loading', boolean: true });

            this.uploadExerciseImage().then(function(done) {

                self.uploadExerciseFiles().then(function(result) {


                    var exercise = {
                        course: self.courseId,
                        title: self.exercise.title,
                        dueDate: self.exercise.dueDate,
                        description: self.exerciseDescription,
                        videoId: self.video.id,
                        image: self.exerciseImage,
                        fileRequired: self.exercise.fileRequired,
                        files: self.exerciseFiles
                    }

                    exercises.create(self, exercise).then(function(response) {
                        if (response.data.success === true) {
                            toastr.success('Exercise Added');
                            self.exercise = {};
                            self.getVideo();

                        } else {
                            toastr.error(response.data.message);
                        }
                        self.$store.dispatch('toggle', { type: 'loading', boolean: false });

                    });

                }).catch(function(e) {
                    console.log(e);
                    alert('ERROR');
                    self.$store.dispatch('toggle', { type: 'loading', boolean: false });
                });


            }).catch(function(e) {
                console.log(e);
                alert('ERROR')
                self.$store.dispatch('toggle', { type: 'loading', boolean: false });

            })
        },
        uploadExerciseImage(event) {
            var self = this;
            return new Promise(function(resolve, reject) {

                if (!event && self.$refs.exerciseImage.files.length < 1) {
                    resolve(true);
                }

                var file;

                if (event) {
                    file = event.target.files[0];
                } else {
                    file = self.$refs.exerciseImage.files[0];
                }

                var filename = file.name.split('.')[0] + '_' + new Date().getMilliseconds() + '.' + file.name.split('.').pop();

                aws.signature(file, filename, 'ttto-exercise-images').then(function(signature) {
                    aws.getPresignedUrl(self, 'ttto-exercise-images', filename, 'putObject').then(function(presignedUrl) {
                        aws.upload(file, filename, signature, 'ttto-exercise-images', presignedUrl.data).then(function(response) {
                            if (event) {
                                self.video.exercise.image = response.url;
                            } else {
                                self.exerciseImage = response.url;
                            }
                            resolve(true)
                        }).catch(function(error) {
                            console.log(error);
                            resolve(false)
                        });
                    });
                });

            });
        },
        deleteResource(file, index) {
            var self = this;
            files.delete(self, file).then(function(response) {
                if (response.data.success === true) {
                    toastr.success('Resource deleted');
                    self.video.resources.splice(index, 1);
                } else {
                    toastr.error('Error deleting resource');
                }
            });
        },
        async uploadResource() {
            const newFile = {
                name: this.file.name,
                description: this.file.description,
                courseId: this.courseId,
                instanceId: this.instanceId,
                instructorId: localStorage.account_id,
                week: this.video.week,
                videoId: this.video.id
            }

            if (this.file.url) {
                newFile.url = this.file.url;

            } else {
                // this is an upload, not a link

                const file = this.$refs.file.files[0];

                let name = file.name;
                const ext = file.name.split('.').pop();
                name = name.split('.');
                name.pop();
                name = name.join('');

                if (ext !== 'zip') {
                    toastr.error('Please zip your assets before uploading them');
                    return false;
                }
                const num = Math.floor(Math.random() * 90) + 10;
                const filename = `${name}_${num}.${ext}`;
                const bucket = 'ttto-course-files';

                let res;
                const sig = await aws.signature(file, filename, bucket);
                res = await aws.upload(file, filename, sig, bucket);
                res = await aws.getPresignedUrl(this, bucket, filename, 'getObject');


                newFile.url = res.data;
            }

            files.add(this, newFile).then(() => {
                this.file = {};
                toastr.success('Resource added');
                this.getVideo();
            });
        },
        getMarkers(player) {
            player.markers({
                markers: [],
                onMarkerReached: function(event) {
                    self.selectedComment = event;
                }
            });
        },
        getAnnotations(player) {
            var self = this;
            var annotations = [];

            self.selectedChapter.annotations.forEach(function(annotation) {

                var content = '<p class="annotation">' + annotation.text;
                // content +='<i class="fa fa-ban" data-id="' + annotation.id + '"></i>';
                // content += '<i class="fa fa-edit" data-id="' + annotation.id + '"></i></p>';

                content += '<div class="buttons">';

                if (annotation.controlKeys) {
                    annotation.controlKeys.forEach(function(key) {
                        content += '<span class="control controlkey ' + key + '">' + key + '</span>';
                    });
                }

                if (annotation.hotKeys) {
                    content += '<span class="control controlkey ' + annotation.hotKeys + '">' + annotation.hotKeys + '</span>';
                }

                if (annotation.mouseButtons) {
                    annotation.mouseButtons.forEach(function(button) {
                        content += '<span class="control mousebutton ' + button + '"></span>';
                    })
                }

                content += '</div>';

                annotations.push({
                    content: content,
                    start: annotation.time,
                    end: annotation.time + 5,
                    align: 'bottom-' + annotation.position
                });

            });

            player.overlay({
                overlays: annotations
            });
        },
        updateAnnotation() {
            var self = this;
            var annotation = this.annotation;
            annotation.controlKeys = this.controlKeys;
            annotation.mouseButtons = this.mouseButtons;
            annotation.hotKeys = this.hotKeys;

            videos.updateAnnotation(this, annotation).then(function(response) {
                if (response.data.success === true) {
                    toastr.success('Flyout updated');
                    self.annotation = {
                        text: '',
                        hotKeys: [],
                        time: null,
                        position: 'left'
                    }
                    self.editMode = false;
                    self.controlKeys = [], self.mouseButtons = [], self.hotKeys = null;
                    self.getVideo().then(() => {
                        self.getAnnotations(self.$refs.videoPlayer.getVjsPlayer());
                    });

                } else {
                    toastr.error('Error updating flyout');
                }
            });
        },
        addAnnotation() {
            var self = this;
            var annotation = this.annotation;
            delete annotation.id;
            annotation.controlKeys = this.controlKeys;
            annotation.mouseButtons = this.mouseButtons;
            annotation.hotKeys = this.hotKeys;
            annotation.chapterId = this.selectedChapter.id;
            annotation.time = this.currentTime;
            if (!annotation.time) annotation.time = 0;


            videos.addAnnotation(this, annotation).then(function(response) {
                if (response.data.success === true) {
                    toastr.success('Flyout added');

                    self.annotation = {
                        text: '',
                        hotKeys: [],
                        time: null,
                        position: 'left'
                    }

                    self.controlKeys = [], self.mouseButtons = [], self.hotKeys = null;
                    self.getVideo().then(() => {
                        self.getAnnotations(self.$refs.videoPlayer.getVjsPlayer());
                    });
                } else {
                    toastr.error('Error adding flyout');
                }
            });
        },
        editAnnotation(id) {

            this.editMode = true;
            var annotations = this.selectedChapter.annotations;
            var index = annotations.findIndex(function(a) {
                return a.id == id;
            });

            var annotation = annotations[index];
            this.controlKeys = annotation.controlKeys;
            this.mouseButtons = annotation.mouseButtons;
            this.hotKeys = annotation.hotKeys;

            this.annotation = annotation;
        },
        deleteAnnotation(id) {
            var self = this;
            videos.deleteAnnotation(this, id).then(function() {
                let index = self.selectedChapter.annotations.findIndex(function(a) { return a.id === id });
                self.selectedChapter.annotations.splice(index, 1);
                self.editMode = false;
            });
        },
        onPlayerTimeupdate(player) {
            this.currentTime = player.currentTime();
        },
        reorder(e) {
            var self = this;
            var newOrder = [];
            this.video.video_chapters.forEach(function(chapter, index) {
                newOrder.push({
                    id: chapter.id,
                    order: index + 1
                });
            });
            videos.reorder(this, newOrder);
        },
        regenerateTranscript() {
            var self = this;

            let transcriptId = null;
            let createNewTranscript = false;

            if (this.selectedChapter.transcript) {
                transcriptId = this.selectedChapter.transcript.id;
            } else {
                createNewTranscript = true;
            }

            videos.regenerateTranscript(this, this.selectedChapter.id, transcriptId, createNewTranscript, this.video.id).then(function(response) {})
            toastr.success('Regenerating transcript. Please check back in approximately ' + (Math.round((parseInt(this.selectedChapter.duration / 60)) + 1))) + ' minutes';

        },
        rename(chapter, id) {
            videos.update(this, 'title', chapter.title, id).then(function(response) {
                if (response.data.success === true) {
                    toastr.success('Updated');
                } else {
                    toastr.error('Error updating');
                }
            });
            if (chapter.heading !== null) {
                videos.update(this, 'heading', chapter.heading, id).then(function(response) {
                    if (response.data.success === true) {
                        toastr.success('Updated');
                    } else {
                        toastr.error('Error updating');
                    }
                });
            }
        },
        deleteChapter(id, index) {
            var self = this;

            this.$swal({
                title: 'Are you sure?',
                text: 'Are you sure you want to delete this chapter?',
                type: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, delete it!',
                cancelButtonText: 'No, keep it'
            }).then(function() {
                videos.deleteChapter(self, id).then(function(response) {
                    if (response.data.success === true) {
                        self.$swal(
                            'Deleted!',
                            'Your chapter has been deleted.',
                            'success'
                        );
                        self.video.video_chapters.splice(index, 1);
                        self.$nextTick(() => {
                            const newOrder = [];
                            self.video.video_chapters.forEach(function(chapter, index) {
                                chapter.order = index + 1;
                                newOrder.push({
                                    id: chapter.id,
                                    order: index + 1
                                });
                            });
                            videos.reorder(this, newOrder);
                        });
                    } else {
                        self.$swal(
                            'Error',
                            'Error deleting chapter.',
                            'error'
                        );
                    }
                })

            }, function(dismiss) {

            });
        },
        replaceVideo(e, index, id) {
            var self = this;
            var file = e.target.files[0];
            const filename = uuidV4() + '.' + file.name.split('.').pop();
            const bucket = 'ttto-course-videos-raw';

            aws.signature(file, filename, bucket).then(function(signature) {

                aws.getPresignedUrl(self, bucket, filename, 'putObject').then(function(presignedUrl) {

                    aws.upload(file, filename, signature, bucket, presignedUrl.data).then(function(response) {

                        self.video.video_chapters[index].url = response.url;
                        self.videoPlayerUrl = response.url;
                        videos.update(self, 'url', response.url, id);

                    });

                });
            });

        },
        updateText(event, index) {
            var text = event.currentTarget.innerHTML;
            text = text.replace(/\&nbsp;/g, ' ').replace(/\&amp;/g, ' ').replace(/<br>/g, '');
            var alreadyChanged = this.transcriptChanges.findIndex(function(c) { return c.index === index });
            if (alreadyChanged > -1) {
                this.transcriptChanges[alreadyChanged].text = text;
            } else {
                this.transcriptChanges.push({
                    index: index,
                    text: text
                });
            }
        },
        updateTranscript() {
            var self = this;
            this.transcriptChanges.forEach(function(phrase) {
                self.selectedChapter.transcript.text[phrase.index].text = phrase.text;

            });
            videos.updateTranscript(self, self.selectedChapter.transcript).then(function(response) {
                if (response.data.success === true) {
                    toastr.success('Transcript Updated');
                } else {
                    toastr.error('Error updating transcript');
                }
            })

        },
        chapterAdded(file) {
            var self = this;
            self.chapters.push(file);
        },

        getVideo() {
            var self = this;
            let preselectedChapter = this.selectedChapter;

            return videos.getOne(this, this.videoId, this.instanceId, this.$route.query.week).then((response) => {
                if (response.data.assignment) {
                    response.data.assignment.availableDate = moment(response.data.assignment.availableDate).tz('America/Vancouver').format('YYYY-MM-DD');
                    response.data.assignment.dueDate = moment(response.data.assignment.dueDate).tz('America/Vancouver').format('YYYY-MM-DD');
                }
                self.video = response.data;

                if (response.data.exercise && response.data.exercise.dueDate) self.video.exercise.dueDate = moment(response.data.exercise.dueDate).tz('America/Vancouver').format('YYYY-MM-DD');

                if (response.data.exercise) self.exerciseDescription = response.data.exercise.description;

                if (response.data.description) self.videoDescription = response.data.description;

                if (preselectedChapter) {
                    self.selectedChapter = self.video.video_chapters.find(c => c.id === preselectedChapter.id);
                    self.getComments();
                } else {
                    if (this.$route.query.chapter) {
                        const selectedChapter = this.video.video_chapters.find(c => c.id === parseInt(this.$route.query.chapter));
                        if (selectedChapter) {
                            this.selectChapter(selectedChapter);
                            this.getComments();
                        }
                    } else {
                        if (this.video.video_chapters && this.video.video_chapters.length > 0) {
                            this.selectChapter(this.video.video_chapters[0]);
                            this.getComments();
                        }
                    }
                }
                if (this.$route.query.tab) {
                    this.selectedTab = this.$route.query.tab;
                }
                return true;

            });
        },
        addComment(parentId, commenter) {
            var self = this;

            var timestamp = self.$refs.videoPlayer.getVjsPlayer().currentTime();
            var comment = {
                timestamp: timestamp,
                userId: localStorage.account_id,
                chapterId: self.selectedChapter.id,
                videoId: self.videoId,
                courseTitle: self.video.course.name,
                videoTitle: self.video.title,
                chapterTitle: self.selectedChapter.title,
                instanceId: self.instanceId,
                courseId: self.courseId,
                week: self.$route.query.week
            }

            if (parentId) {
                comment.parentId = parentId;
                comment.text = self.commentReply[parentId];
                comment.commenter = commenter.id;
            } else {
                comment.text = self.newComment
            }

            videos.addComment(self, comment).then(function(response) {
                if (response.data.id) {
                    toastr.success('Comment added');
                    self.newComment = null;
                    self.commentReply = {};
                    self.getComments();
                } else {
                    toastr.error('Error adding comment');
                }

            });

        },
        getComments() {
            var self = this;
            self.comments = null;
            videos.getComments(self, self.selectedChapter.id).then(function(response) {

                var video = self.$refs.videoPlayer.getVjsPlayer();
                if (_.isFunction(video.markers.removeAll)) video.markers.removeAll();

                var topLevel = [];

                var comments = response.data;

                comments.forEach(function(c, i) {
                    if (c.parentId === null) {
                        comments.splice(i, 1);
                        comments.unshift(c)
                    }
                })

                comments = comments.sort(function(a, b) {
                    return a.parentId - b.parentId;
                });

                if (comments && comments.length > 0) {
                    comments.forEach(function(comment) {
                        if (!comment.parentId) {
                            comment.children = []
                            topLevel.push(comment);
                        } else {
                            var index = topLevel.findIndex(function(x) { return x.id === comment.parentId });
                            topLevel[index].children.push(comment);
                        }
                    });

                }

                window.setTimeout(function() {
                    self.comments = topLevel;
                    self.$refs.videoPlayer.getVjsPlayer().markers.add(topLevel);
                }, 1000)
            });

        },
        addChapters() {
            var self = this;
            var count = self.chapters.length;
            var i = 0;

            function uploadChapter() {
                self.uploadVideoFile(self.chapters[0], i, 'ttto-course-videos-raw').then(function(response) {
                    i++;

                    if (i === count) {
                        self.chapters = [];
                        self.chaptersToAdd = [];
                    } else {
                        uploadChapter(i);
                    }
                });
            }

            uploadChapter();

        },
        uploadVideoFile(file, index) {
            var self = this;
            var uuid = uuidV4();
            const filename = uuid + '.' + file.name.split('.').pop();

            return new Promise(function(resolve, reject) {

                aws.signature(file, filename, 'ttto-course-videos-raw').then(function(signature) {

                    aws.getPresignedUrl(self, 'ttto-course-videos-raw', filename, 'putObject').then(function(presignedUrl) {

                        aws.upload(file, filename, signature, 'ttto-course-videos-raw', presignedUrl.data).then(function(response) {

                            if (response.success === true) {
                                var order;

                                if (self.video.video_chapters.length > 0) {
                                    // this video already has some chapters addded to it so don't start at 1
                                    order = self.video.video_chapters.slice(-1)[0].order + 1;

                                } else {
                                    order = index + 1;
                                }
                                var video = {
                                    order: order,
                                    url: uuid,
                                    title: file.title || 'Chapter ' + (self.video.video_chapters.length + 1).toString(),
                                    videoId: self.videoId,
                                }
                                if (file.heading) { video.heading = file.heading; }

                                videos.addChapters(self, [video], true).then(function(response) {
                                    if (response.data.success === true) {
                                        self.getVideo();
                                        self.chapters.splice(0, 1);
                                        self.$refs.newChapters.dropzone.removeFile(self.$refs.newChapters.dropzone.files[0]);
                                    } else {
                                        toastr.error("Error uploading " + file.title)
                                    }
                                    resolve(true);

                                });
                            } else {
                                alert('Error uploading');
                                resolve(false);
                            }
                        });
                    })

                });
            });
        },
        copyChapter(chapter) {
            const info = chapter.title + '|' + chapter.id + '\n';
            // copy to clipboard
            const el = document.createElement('textarea');
            el.value = info;
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            toastr.success(chapter.title + ' copied to clipboard');
        }
    },
    watch: {
        '$route.params.videoId': function() {
            if (this.videoId === this.$route.params.videoId) {
                return;
            }

            this.courseId = this.$route.params.courseId;
            this.instanceId = this.$route.params.instanceId;
            this.videoId = this.$route.params.videoId;
            this.assignmentData = null;

            this.getVideo();
        },
        '$route.query.chapter': function(val) {
            const selectedChapter = this.video.video_chapters.find(c => c.id === parseInt(val));

            if (selectedChapter) {

                this.selectChapter(selectedChapter);
                this.getComments();
            }
            if (this.$route.query.tab) {
                this.selectedTab = this.$route.query.tab;
            }
        }
    },
    computed: {
        isDirector() {
            return localStorage.roles.includes('OnlineDirector') || localStorage.roles.includes('CampusDirector') || localStorage.roles.includes('CourseAdmin') || localStorage.roles.includes('Director');
        }
    }
}

</script>
<style lang="scss" scoped>
.clear-me {
    clear: both;
}

.synced-lessons {
    list-style: disc;
    padding-left: 3rem;
    margin-top: 1rem;
    margin-bottom: 1rem;
}

.sku {
    display: block;
    clear: both;
    margin-bottom: 1rem;
    font-size: 1.8rem;
}

.buttons {
    margin-top: 2rem;
}

.summary {
    textarea {
        height: 220px !important;
    }
}

table.assignment-files {
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;

    button {
        padding: 4px 8px;
        border: none;
    }

}

.buttons {
    margin-top: 2rem;
}

.chapters .buttons {
    margin-top: 0;
}

input.timecode {
    display: inline;
    width: auto;
}

table.annotations {
    width: 100%;
    border: none;
    background: rgba(0, 0, 0, 0.2) !important;
    margin-top: 2rem;

    tbody,
    tr,
    td {
        border: none !important;
    }

    th,
    td {
        padding: 1rem;
    }

}

.checkbox-wrapper {
    padding: 5px 10px;
}

.resources.panel-body {
    padding: 0 !important;
}

.resources {

    form,
    p {
        padding: 1rem;
    }
}

.resource-table {
    // padding: 0;
    background: #363636;
    // white-space: nowrap;
    // text-align: center;
    // overflow: hidden;
    // text-overflow: ellipses;

    p {
        margin: 0;
        padding: 0;
    }

    a {
        margin: 0;
    }

    i {
        cursor: pointer;
    }

    .buttons {
        margin: 10px 0;

    }

    li {
        margin-bottom: 1rem;
        padding-bottom: 1rem;
        border-bottom: 1px solid rgba(100, 100, 100, 0.5);
    }

    button {
        padding: 0 5px 2px 5px;
    }

    // tr {
    //     background: #363636;
    // }
}

.video-player-wrapper {
    margin-bottom: 2rem;
    padding-right: 0 !important;

    &>.panel-body {
        padding: 0 !important;
    }

    &>.panel {
        margin-top: 2rem;
    }

}

.required {
    color: red;
    position: relative;
    top: -5px;
}

.video-player-placeholder {
    width: 100%;
}

.comments {
    &>li {
        margin-bottom: 2rem;
    }

    margin:1rem auto 2rem;

    .author {
        margin: 0;
    }

    .author,
    .author span {
        font-size: 1.2rem;
    }

    li {
        margin-bottom: 2rem;
        //border-bottom:solid 1px #666;
    }
}

.replies {
    padding-left: 2rem;
    margin-top: 1rem;
}

.form-control:not([type="file"]) {
    background: rgba(0, 0, 0, .2) !important;
}

// .breadcrumbs {
//     padding:1rem 0;
//     margin-bottom: 2rem;
//     li {
//         float:left;
//         color:#fff;
//         margin-right:1rem;
//         a {
//             margin-right:.5rem;
//         }
//         a, span {
//             color:#fff;
//         }
//     }
// }
.panel-body {

    padding: 1rem 2rem !important;
}

.exercise {
    span {
        background: transparent !important;
    }

    form {
        padding: 1rem;
    }

    textarea {
        height: 150px;
    }

    input[type=checkbox] {
        width: 20px;
        height: 20px;
        position: relative;

        margin-right: 1rem;
        float: left;
    }

    label {
        float: left;
    }

}

input[type="file"] {
    max-width: 125px;

}

label[for="lmb"] {
    background: url('../assets/img/lmb.png');
}

label[for="cmb"] {
    background: url('../assets/img/cmb.png');
}

label[for="rmb"] {
    background: url('../assets/img/rmb.png');
}

label.mouse {
    width: 30px;
    height: 42px;
    background-size: 100% 100%;
    background-position: -1px;
    position: relative;
    top: 2rem;
}

.annotations .buttons {
    padding: 2rem 0;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: space-around;
    align-content: center;
    align-items: center;
    padding-bottom: 2rem;
}

.chapters-panel {
    padding-left: 1rem !important;
}

.chapters {

    word-break: break-all; // account for super long filenames

    li>div {
        padding: 0;
    }

    input.selected {
        border: 1px solid #ccc !important;
    }

    i,
    span {
        color: #fff;
    }

    input[type=file] {
        display: none;
    }

    .form-control {
        border-bottom: none !important;
    }

    li {
        list-style-type: none;
        margin-bottom: 2rem;

    }

    .buttons {
        padding: 1rem;
        display: flex;
        align-items: center;
        font-size: 1.4rem;
        justify-content: space-between;
        gap: 8px;
    }

    .handle {
        padding: .5rem;
    }
}

#newChapters+.chapters input[type="text"] {
    margin-bottom: 2rem;
}

.exercise-image-upload {
    clear: both;
}

.remove-lesson-file {
    margin-right: 5px;
    margin-bottom: 10px;
}

</style>
<style lang="scss">
.vue-dropzone .dz-preview .dz-remove {
    bottom: 0 !important;
}

.dropzone {
    margin-bottom: 3rem;
}

textarea.form-control {
    background: transparent !important;
    color: #fff !important;
    border: 1px solid #fff;
}

.dz-preview .dz-progress {
    display: none !important;
}

.chapters li {
    cursor: pointer;
}

.dropzone.dz-started .dz-message {
    display: block !important;
}

.vue-dropzone,
.vue-dropzone:hover {
    background: #212121;
    color: #ededed;
}

.transcript .text textarea {
    background: transparent;
    border: none;
    resize: none;
    color: #fff;
    width: 100%;
}

.video-js {
    overflow: hidden;
}

input.key {
    display: none;

    &:checked+label.key {
        background: #ededed;
    }

    &:checked+label.mouse {
        border: 2px solid #ededed;
        border-radius: 11px 11px 14px 14px;
    }
}

label.key {
    border: 2px solid #fff;
    display: inline-block;
    padding: .5rem 1rem;
    border-radius: 5px;
}

.hotkey.form-control {
    display: inline;
    width: 6rem;
    margin: 0 1rem;
}

.vjs-overlay .fa-edit {
    position: absolute;
    top: 1rem;
    right: 3rem;
}

.student-view {
    margin-bottom: 1rem;
}

.delete-button {
    background: red;
    color: #fff;
}

.duration {
    white-space: nowrap
}

</style>



// WEBPACK FOOTER //
// VideoAdmin.vue?ae51fdf6